import React, { useState, useRef, useEffect } from 'react';
import { Task } from '../api/client';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (taskId: number, photo: File) => Promise<void>;
  tasks: Task[];
}

export function AddPhotoToTaskModal({ isOpen, onClose, onSubmit, tasks }: Props) {
  const [step, setStep] = useState<'select' | 'upload'>('select');
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Filter tasks: only OPEN/DONE and without photos
  const eligibleTasks = tasks.filter(
    t => (t.status === 'open' || t.status === 'done') && !t.photo_url
  );

  // Escape key handler
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isSubmitting) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, isSubmitting, onClose]);

  if (!isOpen) return null;

  const handleTaskSelect = (taskId: number) => {
    setSelectedTaskId(taskId);
    setStep('upload');
    setError(null);
  };

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'];
    if (!validTypes.includes(file.type) && !file.name.toLowerCase().endsWith('.heic')) {
      setError('Please select a valid image file (JPG, PNG, WebP, or HEIC)');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('Photo must be less than 10MB');
      return;
    }

    setError(null);
    setPhoto(file);

    const reader = new FileReader();
    reader.onload = (e) => {
      setPhotoPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleRemovePhoto = () => {
    setPhoto(null);
    setPhotoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!selectedTaskId || !photo) {
      setError('Please select a task and upload a photo');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onSubmit(selectedTaskId, photo);
      // Reset state
      setStep('select');
      setSelectedTaskId(null);
      setPhoto(null);
      setPhotoPreview(null);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload photo');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    setStep('select');
    setPhoto(null);
    setPhotoPreview(null);
    setError(null);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isSubmitting) {
      onClose();
    }
  };

  const handleClose = () => {
    setStep('select');
    setSelectedTaskId(null);
    setPhoto(null);
    setPhotoPreview(null);
    setError(null);
    onClose();
  };

  const selectedTask = tasks.find(t => t.id === selectedTaskId);

  return (
    <div
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '16px'
      }}
    >
      <div style={{
        background: '#fff',
        borderRadius: '8px',
        width: '100%',
        maxWidth: '500px',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid #eee',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ margin: 0, fontSize: '18px' }}>
            {step === 'select' ? 'Select Task' : 'Upload Photo'}
          </h2>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
              padding: '0 4px'
            }}
          >
            &times;
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px' }}>
          {error && (
            <div style={{
              background: '#fee',
              color: '#c00',
              padding: '10px 12px',
              borderRadius: '4px',
              marginBottom: '16px',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          {step === 'select' ? (
            <>
              {eligibleTasks.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#666', padding: '20px 0' }}>
                  No tasks available for photo upload.
                  <br />
                  <span style={{ fontSize: '13px' }}>
                    Tasks must be open or done, and not already have a photo.
                  </span>
                </div>
              ) : (
                <div>
                  <p style={{ margin: '0 0 12px', fontSize: '14px', color: '#666' }}>
                    Select a task to add a photo:
                  </p>
                  <div style={{ maxHeight: '300px', overflow: 'auto' }}>
                    {eligibleTasks.map((task, index) => (
                      <div
                        key={task.id}
                        id={`task-option-${task.id}`}
                        tabIndex={0}
                        role="button"
                        aria-pressed={selectedTaskId === task.id}
                        onClick={() => handleTaskSelect(task.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleTaskSelect(task.id);
                          } else if (e.key === 'ArrowDown' && index < eligibleTasks.length - 1) {
                            e.preventDefault();
                            const nextOption = document.getElementById(`task-option-${eligibleTasks[index + 1].id}`);
                            nextOption?.focus();
                          } else if (e.key === 'ArrowUp' && index > 0) {
                            e.preventDefault();
                            const prevOption = document.getElementById(`task-option-${eligibleTasks[index - 1].id}`);
                            prevOption?.focus();
                          } else if (e.key === 'Escape') {
                            e.preventDefault();
                            handleClose();
                          }
                        }}
                        style={{
                          padding: '12px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          marginBottom: '8px',
                          cursor: 'pointer',
                          transition: 'background 0.2s',
                          outline: 'none'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                        onFocus={(e) => e.currentTarget.style.background = '#f5f5f5'}
                        onBlur={(e) => e.currentTarget.style.background = '#fff'}
                      >
                        <div style={{ fontWeight: 500, marginBottom: '4px' }}>
                          {task.description}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          Status: {task.status.toUpperCase()}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Show selected task info */}
              {selectedTask && (
                <div style={{
                  background: '#f5f5f5',
                  padding: '12px',
                  borderRadius: '4px',
                  marginBottom: '16px'
                }}>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                    Selected Task:
                  </div>
                  <div style={{ fontWeight: 500 }}>{selectedTask.description}</div>
                </div>
              )}

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>
                  Photo <span style={{ color: '#c00' }}>*</span>
                </label>

                {photoPreview ? (
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <img
                      src={photoPreview}
                      alt="Preview"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '200px',
                        borderRadius: '4px',
                        border: '1px solid #ddd'
                      }}
                    />
                    <button
                      onClick={handleRemovePhoto}
                      style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        background: 'rgba(0, 0, 0, 0.6)',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '50%',
                        width: '28px',
                        height: '28px',
                        cursor: 'pointer',
                        fontSize: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      &times;
                    </button>
                  </div>
                ) : (
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/heic,.heic"
                      onChange={handlePhotoSelect}
                      style={{ display: 'none' }}
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      style={{
                        padding: '10px 16px',
                        background: '#f0f0f0',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <span style={{ fontSize: '18px' }}>+</span>
                      Select Photo
                    </button>
                    <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#666' }}>
                      JPG, PNG, WebP, or HEIC. Max 10MB.
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 20px',
          borderTop: '1px solid #eee',
          display: 'flex',
          justifyContent: 'space-between',
          gap: '12px'
        }}>
          <div>
            {step === 'upload' && (
              <button
                onClick={handleBack}
                disabled={isSubmitting}
                style={{
                  padding: '10px 20px',
                  background: '#fff',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Back
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              style={{
                padding: '10px 20px',
                background: '#fff',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Cancel
            </button>
            {step === 'upload' && (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || !photo}
                style={{
                  padding: '10px 20px',
                  background: isSubmitting || !photo ? '#ccc' : '#007bff',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: isSubmitting || !photo ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 500
                }}
              >
                {isSubmitting ? 'Uploading...' : 'Upload Photo'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
